/**
 * Website Analytics - Shared tracking for all public websites
 * 
 * Tracks page visits and funnel events:
 * - page_visit: Initial page load (deduplicated per session per day)
 * - demo_clicked: User clicked a CTA to open the demo
 * - demo_started: User started the interactive demo
 * - email_submitted: User submitted their email after demo
 * - contact_submitted: User submitted the contact form
 * 
 * Features:
 * - Developer exclusion: Visit /?_exclude to stop being tracked
 * - UTM parameter capture: Automatically captures utm_source, utm_medium, utm_campaign
 * - Session-based deduplication: One page_visit per session per day
 * 
 * Usage:
 * 1. Call initWebsiteAnalytics({ apiUrl }) on app startup
 * 2. Call trackPageVisit(siteType) on landing page mount
 * 3. Call trackEmailSubmitted() etc. for other events
 */

import { SiteType, DEFAULT_SITE_TYPE } from '@wfvr/types';

// Storage keys
const EXCLUDE_KEY = '_wa_exclude';
const SESSION_KEY = '_wa_session';
const TRACKED_KEY = '_wa_tracked_today';
const UTM_KEY = '_wa_utm';
const SITE_TYPE_KEY = '_wa_site_type';

// Module-level configuration
let apiUrl: string = 'https://api.selna.ai/api';

type AnalyticsEvent =
  | 'page_visit'
  | 'demo_clicked'
  | 'demo_started'
  | 'email_submitted'
  | 'contact_submitted';

interface UTMParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
}

interface AnalyticsConfig {
  /** Base API URL (e.g., 'https://api.selna.ai/api') */
  apiUrl: string;
}

interface TrackEventOptions {
  /** Which site/vertical this event is from */
  siteType?: SiteType;
}

// ============================================================================
// Developer Exclusion
// ============================================================================

/**
 * Check if current user is excluded from analytics
 */
export function isExcluded(): boolean {
  try {
    return localStorage.getItem(EXCLUDE_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * Exclude current browser from analytics tracking
 * Call this or visit /?_exclude to stop being tracked
 */
export function excludeFromAnalytics(): void {
  try {
    localStorage.setItem(EXCLUDE_KEY, 'true');
    console.info('[Analytics] You are now excluded from website analytics tracking');
  } catch (e) {
    console.warn('[Analytics] Could not save exclusion preference:', e);
  }
}

/**
 * Re-include current browser in analytics tracking
 * Useful for testing after exclusion
 */
export function includeInAnalytics(): void {
  try {
    localStorage.removeItem(EXCLUDE_KEY);
    console.info('[Analytics] You are now included in website analytics tracking');
  } catch (e) {
    console.warn('[Analytics] Could not remove exclusion preference:', e);
  }
}

/**
 * Check URL for ?_exclude parameter and save exclusion
 * Called automatically by initWebsiteAnalytics
 */
function checkExcludeParam(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.has('_exclude')) {
      excludeFromAnalytics();
      // Clean URL by removing the parameter
      params.delete('_exclude');
      const newUrl = params.toString() 
        ? `${window.location.pathname}?${params.toString()}`
        : window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  } catch {
    // Ignore errors (SSR, etc.)
  }
}

// ============================================================================
// Session Management
// ============================================================================

/**
 * Get or create a session ID for this browser session
 * Session ID persists in sessionStorage (cleared when browser closes)
 */
function getSessionId(): string {
  try {
    let sessionId = sessionStorage.getItem(SESSION_KEY);
    if (!sessionId) {
      // Generate a simple random ID
      sessionId = `wa_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
      sessionStorage.setItem(SESSION_KEY, sessionId);
    }
    return sessionId;
  } catch {
    // Fallback for when sessionStorage is not available
    return `wa_fallback_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  }
}

/**
 * Get the current analytics session ID
 * This can be used to link website analytics with session-tracking.
 * 
 * Creates a session ID if one doesn't exist yet, ensuring the ID is always
 * available when building demo URLs (before any tracking events are fired).
 * 
 * @returns The session ID (always returns a valid ID, creates one if needed)
 */
export function getAnalyticsSessionId(): string {
  return getSessionId();
}

/**
 * Force the analytics session ID for this tab/session.
 *
 * Needed because the demo often opens in a new tab, which has a fresh sessionStorage.
 * We pass the landing sessionId via URL param (`wa`) and rehydrate it here.
 */
export function setAnalyticsSessionId(sessionId: string): void {
  try {
    if (!sessionId || typeof sessionId !== 'string') return;
    sessionStorage.setItem(SESSION_KEY, sessionId);
  } catch {
    // Ignore
  }
}

/**
 * Check if page_visit was already tracked today for this session
 */
function wasPageVisitTrackedToday(): boolean {
  try {
    const tracked = sessionStorage.getItem(TRACKED_KEY);
    if (!tracked) return false;
    
    const today = new Date().toISOString().split('T')[0];
    return tracked === today;
  } catch {
    return false;
  }
}

/**
 * Mark page_visit as tracked for today
 */
function markPageVisitTracked(): void {
  try {
    const today = new Date().toISOString().split('T')[0];
    sessionStorage.setItem(TRACKED_KEY, today);
  } catch {
    // Ignore
  }
}

// ============================================================================
// UTM Parameters
// ============================================================================

/**
 * Capture UTM parameters from URL and store them
 * Called automatically by initWebsiteAnalytics
 */
function captureUTMParams(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    const utm: UTMParams = {};
    
    if (params.has('utm_source')) utm.utm_source = params.get('utm_source') || undefined;
    if (params.has('utm_medium')) utm.utm_medium = params.get('utm_medium') || undefined;
    if (params.has('utm_campaign')) utm.utm_campaign = params.get('utm_campaign') || undefined;
    
    if (Object.keys(utm).length > 0) {
      sessionStorage.setItem(UTM_KEY, JSON.stringify(utm));
      console.debug('[Analytics] Captured UTM params:', Object.keys(utm));
    }
  } catch {
    // Ignore
  }
}

/**
 * Get stored UTM parameters
 */
function getUTMParams(): UTMParams {
  try {
    const stored = sessionStorage.getItem(UTM_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

// ============================================================================
// Site Type Management
// ============================================================================

/**
 * Set the site type for this session
 * Call this on landing page load to identify which vertical the user is on
 */
export function setSiteType(siteType: SiteType): void {
  try {
    sessionStorage.setItem(SITE_TYPE_KEY, siteType);
    console.debug(`[Analytics] Set site type: ${siteType}`);
  } catch {
    // Ignore
  }
}

/**
 * Get the current site type for this session
 */
function getSiteType(): SiteType {
  try {
    const stored = sessionStorage.getItem(SITE_TYPE_KEY);
    return (stored as SiteType) || DEFAULT_SITE_TYPE;
  } catch {
    return DEFAULT_SITE_TYPE;
  }
}

// ============================================================================
// Event Tracking
// ============================================================================

/**
 * Build the payload for an analytics event
 */
function buildPayload(event: AnalyticsEvent, options?: TrackEventOptions) {
  const sessionId = getSessionId();
  const utmParams = getUTMParams();
  const siteType = options?.siteType || getSiteType();
  
  return {
    event,
    sessionId,
    siteType,
    ...utmParams,
    referrer: document.referrer || undefined,
    userAgent: navigator.userAgent
  };
}

/**
 * Track an analytics event using sendBeacon (fire-and-forget, reliable on navigation)
 * Use this for click events where the page might navigate away immediately.
 * 
 * @param event - The event type to track
 * @param options - Optional tracking options (e.g., siteType override)
 */
export function trackEventBeacon(event: AnalyticsEvent, options?: TrackEventOptions): void {
  // Skip if excluded
  if (isExcluded()) {
    console.debug(`[Analytics] Skipping ${event} - user excluded`);
    return;
  }
  
  try {
    const payload = buildPayload(event, options);
    const url = `${apiUrl}/website-analytics/track`;
    
    console.debug(`[Analytics] Tracking ${event} via beacon (site: ${payload.siteType})...`);
    
    // Create a Blob with JSON content type - sendBeacon with string sends as text/plain
    const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
    
    // sendBeacon is guaranteed to be sent even if the page is being closed
    const success = navigator.sendBeacon(url, blob);
    
    if (success) {
      console.debug(`[Analytics] Beacon queued for ${event}`);
    } else {
      // Fallback to fetch if sendBeacon fails (e.g., payload too large)
      console.warn(`[Analytics] Beacon failed for ${event}, falling back to fetch`);
      trackEvent(event, options);
    }
  } catch (error) {
    // Fail silently - analytics should never break the app
    console.warn(`[Analytics] Error tracking ${event}:`, error);
  }
}

/**
 * Track an analytics event using fetch (async, awaitable)
 * Use this for events where you need to wait for confirmation (e.g., page_visit)
 * 
 * @param event - The event type to track
 * @param options - Optional tracking options (e.g., siteType override)
 * @returns Promise that resolves when tracking is complete (or fails silently)
 */
export async function trackEvent(event: AnalyticsEvent, options?: TrackEventOptions): Promise<void> {
  // Skip if excluded
  if (isExcluded()) {
    console.debug(`[Analytics] Skipping ${event} - user excluded`);
    return;
  }
  
  // For page_visit, skip if already tracked today
  if (event === 'page_visit' && wasPageVisitTrackedToday()) {
    console.debug('[Analytics] Skipping page_visit - already tracked today');
    return;
  }
  
  try {
    const payload = buildPayload(event, options);
    
    console.debug(`[Analytics] Tracking ${event} (site: ${payload.siteType})...`);
    
    const response = await fetch(`${apiUrl}/website-analytics/track`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });
    
    if (response.ok) {
      // Mark page_visit as tracked for today
      if (event === 'page_visit') {
        markPageVisitTracked();
      }
      console.debug(`[Analytics] Successfully tracked ${event}`);
    } else {
      console.warn(`[Analytics] Failed to track ${event}:`, response.status);
    }
  } catch (error) {
    // Fail silently - analytics should never break the app
    console.warn(`[Analytics] Error tracking ${event}:`, error);
  }
}

/**
 * Track page visit - convenience function
 * Should be called once on initial landing page load
 * 
 * @param siteType - Which site/vertical this is (defaults to session value or main_landing_page)
 */
export function trackPageVisit(siteType?: SiteType): void {
  // If siteType is provided, also store it for future events in this session
  if (siteType) {
    setSiteType(siteType);
  }
  trackEvent('page_visit', { siteType });
}

/**
 * Track demo started - call when user clicks to start the interactive demo
 * Uses sendBeacon to ensure tracking succeeds even if user navigates away immediately
 */
export function trackDemoStarted(): void {
  trackEventBeacon('demo_started');
}

/**
 * Track demo clicked - call when user clicks a CTA that opens the demo (often a new tab).
 * This is NOT the same as actually starting the demo.
 */
export function trackDemoClicked(): void {
  trackEventBeacon('demo_clicked');
}

/**
 * Track email submitted - call when user submits email after demo
 * Uses sendBeacon to ensure tracking succeeds even when page navigates away immediately
 */
export function trackEmailSubmitted(): void {
  trackEventBeacon('email_submitted');
}

/**
 * Track contact form submitted - call when user submits contact form
 */
export function trackContactSubmitted(): void {
  trackEvent('contact_submitted');
}

// ============================================================================
// Initialization
// ============================================================================

/**
 * Initialize website analytics
 * Call this once on app startup (e.g., in main.tsx)
 * 
 * @param config - Configuration object with apiUrl
 * 
 * Does the following:
 * 1. Stores the API URL for tracking requests
 * 2. Checks for ?_exclude parameter
 * 3. Captures UTM parameters
 * 
 * NOTE: Does NOT track page_visit automatically - that should be
 * called explicitly on the landing page to avoid tracking internal pages.
 */
export function initWebsiteAnalytics(config: AnalyticsConfig): void {
  // Store configuration
  apiUrl = config.apiUrl;
  
  // Check for exclusion param first
  checkExcludeParam();
  
  // Capture UTM params before they might be removed by routing
  captureUTMParams();
}
